import {useCallback, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import * as http from '../../../api/http'
import {ENDPOINTS} from '../../../api/config'
import {KEYS} from '../../../constants/keysStorage'
import {State} from '../../../reducers'
import {generalOpenToastSuccess} from '../../../actions/general.actions'
import {esNormativaV2} from '../../../utils/normativa.utils'
import {normalizarSerie, validarSerieDocumentoV2} from '../../../utils/series.utils'

const TIEMPO_LIMITE_PETICION = 20000

const MENSAJE_SIN_RESPUESTA =
  'El servicio de series no respondió. Espere un momento y presione Reintentar.'

export interface SerieDocumento {
  idsucursaldocumento: any
  idsucursal: any
  iddocumento: any
  serie: string
  numeroactual: string
  numeroinicial: string
  resolucion: string
  estado: any
  nombreSucursal: string
  nombreDocumento: string
  correlativosEmitidos: number
  cumpleFormatoV2: boolean
}

const conTiempoLimite = <T>(peticion: Promise<T>): Promise<T> => {
  return new Promise((resolver, rechazar) => {
    const temporizador = setTimeout(
      () => rechazar(new Error(MENSAJE_SIN_RESPUESTA)),
      TIEMPO_LIMITE_PETICION
    )
    peticion
      .then((respuesta) => {
        clearTimeout(temporizador)
        resolver(respuesta)
      })
      .catch((error) => {
        clearTimeout(temporizador)
        rechazar(error)
      })
  })
}

const mensajeDeError = (error: any, respaldo: string): string => {
  const mensajeServicio = error?.response?.data?.message
  if (mensajeServicio) return mensajeServicio
  if (error?.message) return error.message
  return respaldo
}

const armarSeries = (
  sucursales: Array<any>,
  documentos: Array<any>,
  tiposDocumento: Array<any>
): Array<SerieDocumento> => {
  const listaSucursales = Array.isArray(sucursales) ? sucursales : []
  const listaDocumentos = Array.isArray(documentos) ? documentos : []
  const listaTipos = Array.isArray(tiposDocumento) ? tiposDocumento : []

  return listaDocumentos.map((documento: any) => {
    const sucursal = listaSucursales.find(
      (item: any) => `${item?.idsucursal}` === `${documento?.idsucursal}`
    )
    const tipoDocumento = listaTipos.find(
      (item: any) => `${item?.iddocumento}` === `${documento?.iddocumento}`
    )
    const serie = normalizarSerie(documento?.serie ?? '')
    const actual = parseInt(documento?.numeroactual ?? '0', 10)
    const inicial = parseInt(documento?.numeroinicial ?? '0', 10)

    return {
      idsucursaldocumento: documento?.idsucursaldocumento,
      idsucursal: documento?.idsucursal,
      iddocumento: documento?.iddocumento,
      serie,
      numeroactual: `${documento?.numeroactual ?? ''}`,
      numeroinicial: `${documento?.numeroinicial ?? ''}`,
      resolucion: `${documento?.resolucion ?? ''}`,
      estado: documento?.estado,
      nombreSucursal: sucursal?.nombre ?? 'Sucursal no identificada',
      nombreDocumento: tipoDocumento?.descripcion ?? tipoDocumento?.nombre ?? 'Documento',
      correlativosEmitidos: isNaN(actual) || isNaN(inicial) ? 0 : Math.max(actual - inicial, 0),
      cumpleFormatoV2: validarSerieDocumentoV2(serie),
    }
  })
}

export const useSeriesDocumentos = () => {
  const dispatch = useDispatch()
  const {tipoDocumentosEmision, datosEmisor} = useSelector((state: State) => state.catalogos)

  const [series, setSeries] = useState<Array<SerieDocumento>>([])
  const [cargando, setCargando] = useState(false)
  const [errorCarga, setErrorCarga] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [consultado, setConsultado] = useState(false)

  const normativaV2 = esNormativaV2(datosEmisor)

  const cargarSeries = useCallback(async () => {
    setCargando(true)
    setErrorCarga('')
    const codigoempresa = localStorage.getItem(KEYS.CODIGO_EMPRESA)

    try {
      const [respuestaSucursales, respuestaDocumentos] = await Promise.all([
        conTiempoLimite<any>(http.get(ENDPOINTS.SERIES_DOCUMENTOS_GET_SUCURSALES, {codigoempresa})),
        conTiempoLimite<any>(http.get(ENDPOINTS.SERIES_DOCUMENTOS_GET_LISTADO, {codigoempresa})),
      ])
      setSeries(
        armarSeries(
          respuestaSucursales?.data ?? [],
          respuestaDocumentos?.data ?? [],
          tipoDocumentosEmision
        )
      )
    } catch (error: any) {
      setSeries([])
      setErrorCarga(mensajeDeError(error, 'No fue posible obtener las series de sus documentos.'))
    } finally {
      setCargando(false)
      setConsultado(true)
    }
  }, [tipoDocumentosEmision])

  const guardarSerie = async (documento: SerieDocumento, serieNueva: string): Promise<string> => {
    setGuardando(true)
    try {
      await conTiempoLimite<any>(
        http.put(ENDPOINTS.SERIES_DOCUMENTOS_PUT_SERIE, {
          codigoempresa: localStorage.getItem(KEYS.CODIGO_EMPRESA),
          idsucursaldocumento: documento.idsucursaldocumento,
          idsucursal: documento.idsucursal,
          iddocumento: documento.iddocumento,
          serie: normalizarSerie(serieNueva),
        })
      )
      setSeries((anteriores) =>
        anteriores.map((item) =>
          item.idsucursaldocumento === documento.idsucursaldocumento
            ? {
                ...item,
                serie: normalizarSerie(serieNueva),
                cumpleFormatoV2: validarSerieDocumentoV2(serieNueva),
              }
            : item
        )
      )
      dispatch(
        generalOpenToastSuccess('La serie del documento quedó actualizada.', 'Serie guardada')
      )
      return ''
    } catch (error: any) {
      return mensajeDeError(
        error,
        'No fue posible guardar la serie. Intente de nuevo en unos minutos.'
      )
    } finally {
      setGuardando(false)
    }
  }

  return {
    series,
    cargando,
    errorCarga,
    guardando,
    consultado,
    normativaV2,
    cargarSeries,
    guardarSerie,
  }
}
